/**
 * Refactored Agent Registration Page
 *
 * Uses state machine pattern via RegistrationContext for cleaner, more maintainable code.
 * All business logic is in the context - this component is purely presentational.
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRegistration } from '../../contexts/RegistrationContext';
import logo from '../../assets/ew-color-logo_crop.png';
import './RegisterPage.css';

const RegisterPage: React.FC = () => {
	const [searchParams] = useSearchParams();
	const email = searchParams.get('email') || '';
	const token = searchParams.get('token') || '';

	const {
		state,
		isLoading,
		error,
		validateInvitation,
		submitPassword,
		clearError
	} = useRegistration();

	// Form state
	const [formData, setFormData] = useState({
		password: '',
		confirmPassword: '',
		phone: ''
	});

	/**
	 * Validate invitation on mount
	 */
	useEffect(() => {
		if (email && token && state.type === 'IDLE') {
			validateInvitation(email, token);
		}
	}, [email, token, state.type, validateInvitation]);

	/*
	 * The phone field is no longer pre-filled. validateAgentInvitation returns
	 * only the name, email and role - anything more would be handing out agent
	 * record details before the invitee has proven who they are.
	 */

	/**
	 * Redirect to dashboard when complete
	 * Use window.location for full page reload so AuthContext loads fresh
	 */
	useEffect(() => {
		if (state.type === 'COMPLETE') {
			setTimeout(() => {
				window.location.href = '/agent/dashboard';
			}, 2000);
		}
	}, [state]);

	/**
	 * Handle form input changes
	 */
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData(prev => ({ ...prev, [name]: value }));
		if (error) clearError();
	};

	/**
	 * Handle password form submission
	 */
	const handlePasswordSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		await submitPassword(email, token, formData.password, formData.confirmPassword, formData.phone);
	};

	/**
	 * Render loading state
	 */
	if (state.type === 'IDLE' || state.type === 'VALIDATING_INVITATION') {
		return (
			<div className="register-page">
				<div className="register-container">
					<div className="register-header">
						<a href="/" className="register-logo-link">
							<img src={logo} alt="EquityWorkx" className="register-logo" />
						</a>
					</div>
					<div className="loading-spinner"></div>
					<p style={{ textAlign: 'center', color: '#64748b', marginTop: '1rem' }}>
						Validating invitation...
					</p>
				</div>
			</div>
		);
	}

	/**
	 * Render invalid invitation state
	 */
	if (state.type === 'INVITATION_INVALID') {
		return (
			<div className="register-page">
				<div className="register-container">
					<div className="register-header">
						<a href="/" className="register-logo-link">
							<img src={logo} alt="EquityWorkx" className="register-logo" />
						</a>
					</div>
					<div className="error-message">
						<h2>Invalid Invitation</h2>
						<p>{state.error}</p>
						<p>Please contact your administrator for a new invitation.</p>
					</div>
				</div>
			</div>
		);
	}

	/**
	 * Get agent data from state (available in all valid states)
	 */
	const agent = state.type === 'PASSWORD_SETUP' ||
		state.type === 'COMPLETE'
		? state.agent
		: null;

	if (!agent) {
		return null; // Should never happen
	}

	return (
		<div className="register-page">
			<div className="register-container">
				<div className="register-header">
					<a href="/" className="register-logo-link">
						<img src={logo} alt="EquityWorkx" className="register-logo" />
					</a>
					<h1>Complete Your Registration</h1>
					<p>Welcome, {agent.firstName}!</p>
					{state.type === 'PASSWORD_SETUP' && (
						<p className="subtitle">Create your account password to get started.</p>
					)}
				</div>

				{/* Error Alert */}
				{error && (
					<div className="alert alert-error">
						{error}
					</div>
				)}

				{/* PASSWORD SETUP */}
				{state.type === 'PASSWORD_SETUP' && (
					<form onSubmit={handlePasswordSubmit} className="register-form">
						<div className="form-group">
							<label htmlFor="email">Email</label>
							<input
								type="email"
								id="email"
								value={agent.email}
								disabled
								className="input-disabled"
							/>
						</div>

						<div className="form-group">
							<label htmlFor="role">Role</label>
							<input
								type="text"
								id="role"
								value={
									agent.role === 'super_agent'
										? 'Super Agent'
										: agent.role === 'master_agent'
											? 'Master Agent'
											: 'Agent'
								}
								disabled
								className="input-disabled"
							/>
						</div>

						<div className="form-group">
							<label htmlFor="phone">Phone Number</label>
							<input
								type="tel"
								id="phone"
								name="phone"
								value={formData.phone}
								onChange={handleInputChange}
								placeholder="Enter your phone number"
							/>
						</div>

						<div className="form-group">
							<label htmlFor="password">Password *</label>
							<input
								type="password"
								id="password"
								name="password"
								value={formData.password}
								onChange={handleInputChange}
								placeholder="Enter your password"
								required
								minLength={8}
							/>
							<small className="form-hint">Password must be at least 8 characters long</small>
						</div>

						<div className="form-group">
							<label htmlFor="confirmPassword">Confirm Password *</label>
							<input
								type="password"
								id="confirmPassword"
								name="confirmPassword"
								value={formData.confirmPassword}
								onChange={handleInputChange}
								placeholder="Confirm your password"
								required
							/>
						</div>

						<button
							type="submit"
							className="btn-primary btn-block"
							disabled={isLoading}
						>
							{isLoading ? 'Creating Account...' : 'Complete Registration'}
						</button>
					</form>
				)}

				{/* COMPLETE */}
				{state.type === 'COMPLETE' && (
					<div className="mfa-complete-section">
						<div className="success-icon">✓</div>
						<h3>Registration Complete!</h3>
						<p>Your account has been created successfully.</p>
						<p>Redirecting to your dashboard...</p>
					</div>
				)}

				<div className="register-footer">
					<p>Already have an account? <a href="/agent/login">Sign in</a></p>
				</div>
			</div>
		</div>
	);
};

export default RegisterPage;
